import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Label } from "../components/ui/Label";
import { Textarea } from "../components/ui/Textarea";
import { Card, CardBody } from "../components/ui/Card";
import { api, ApiException } from "../lib/api";

export function CreateCircle() {
  const navigate = useNavigate();
  const [circleName, setCircleName] = useState("");
  const [lovedOneName, setLovedOneName] = useState("");
  const [dob, setDob] = useState("");
  const [address, setAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post("/api/circles", {
        circle_name: circleName,
        loved_one_name: lovedOneName,
        loved_one_dob: dob || null,
        loved_one_address: address || null,
        loved_one_notes: notes || null,
      });
      navigate("/app", { replace: true });
    } catch (err) {
      setError(err instanceof ApiException ? err.message : "Something went wrong.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-full">
      <header className="border-b border-border bg-surface">
        <div className="mx-auto max-w-3xl px-5 h-14 flex items-center">
          <Link to="/app" className="font-semibold tracking-tight">Carol Care</Link>
        </div>
      </header>

      <main className="mx-auto max-w-xl px-5 py-8">
        <Card>
          <CardBody>
            <h1 className="text-2xl font-semibold tracking-tight">Create your care circle</h1>
            <p className="mt-1 text-sm text-text-muted">
              You'll be the admin. You can invite other family members once it's set up.
            </p>

            <form onSubmit={onSubmit} className="mt-6 space-y-5" noValidate>
              <section>
                <h2 className="text-sm font-semibold text-text-muted mb-3 uppercase tracking-wide">Circle</h2>
                <Label htmlFor="circle">Circle name</Label>
                <Input
                  id="circle"
                  required
                  placeholder="Carol's Care Circle"
                  value={circleName}
                  onChange={(e) => setCircleName(e.target.value)}
                />
              </section>

              <section>
                <h2 className="text-sm font-semibold text-text-muted mb-3 uppercase tracking-wide">Loved one</h2>
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="lo-name">Their name</Label>
                    <Input
                      id="lo-name"
                      required
                      placeholder="Carol"
                      value={lovedOneName}
                      onChange={(e) => setLovedOneName(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="lo-dob">Date of birth (optional)</Label>
                    <Input
                      id="lo-dob"
                      type="date"
                      value={dob}
                      onChange={(e) => setDob(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="lo-addr">Home address (optional)</Label>
                    <Textarea
                      id="lo-addr"
                      placeholder="For ride pickups"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      rows={2}
                    />
                  </div>
                  <div>
                    <Label htmlFor="lo-notes">Notes (optional)</Label>
                    <Textarea
                      id="lo-notes"
                      placeholder="Mobility needs, allergies, insurance, anything useful for the family"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={3}
                    />
                  </div>
                </div>
              </section>

              {error && (
                <div role="alert" className="rounded-md border border-danger/30 bg-red-50 text-danger text-sm px-3 py-2">
                  {error}
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <Button type="submit" size="lg" loading={busy}>
                  Create circle
                </Button>
                <Link to="/app">
                  <Button type="button" variant="ghost" size="lg">Cancel</Button>
                </Link>
              </div>
            </form>
          </CardBody>
        </Card>
      </main>
    </div>
  );
}
